import { GameEvent, OperatorEvent } from "../interfaces/theos.interface";
import { IGameSettings, IRoundFinishedData, IRoundStartedData } from "../feim/types";
import { IPausePayload, handleClientSideRcInterval } from "./handleIdlePause";
import { LogLevel, log, warn } from "../utils/log";
import { closeExternalRealityCheck, showExternalRealityCheck } from "./realityCheckIframe";
import { IPostMessageApi } from "../interfaces/postMessageApi.interface";
import { postToOperator } from "../feim/postToOperator";
import { triggerEventInGame } from "../feim/registerEventHandler";
import { version } from "../../package.json";

/**
 * An adapter for a direct connection with Rg PostMessage API, removing the need for a Theos layer for it.
 */

let cachedBalance = 0;
let cachedSoundSetting = -1;

// Directly post an rgMessage
const post = (type: string, payload?: unknown): void =>
{
    postToOperator({rgMessage: type, payload});
};

const getErrors = (): string[] =>
{
    return [];
};

interface IRgApiEvent {
    readonly [key: string]: unknown;
    readonly rgMessage: string;
    readonly payload?: unknown;
}

export class RgPostMessageApiAdapter implements IPostMessageApi
{
    public name = "Rg PostMessage API";

    public constructor ()
    {
        post("gprg_Listening");
    }

    public checkHandshake (message: unknown): boolean
    {
        const data = message as IRgApiEvent;

        if (data.rgMessage === "oprg_Ready") {
            log("Using direct Rg PostMessage API connection", LogLevel.INIT);

            // Not sure if rcinterval is ever used in tandem with Rg API, but handle it just in case
            handleClientSideRcInterval();

            post("gprg_Ready");

            triggerEventInGame(OperatorEvent._INITIALIZED);

            return true;
        }

        return false;
    }

    public processInbound (data: unknown): {feimOpEvent: OperatorEvent; payload?: {}} | undefined
    {
        if (data === undefined) {
            return;
        }

        const rgData = data as {
            readonly [key: string]: unknown;
            readonly rgMessage: string;
            readonly payload?: unknown;
        };

        if (rgData.rgMessage === undefined || !rgData.rgMessage.includes("oprg_"))
        {
            return;
        }

        // Events that need to reach the game are converted to FEIM format so all the listeners work
        switch (rgData.rgMessage)
        {
            case "oprg_GamePause":
                if (rgData.payload !== undefined) {
                    const payload = rgData.payload as IPausePayload;

                    if (payload.autoPlayOnly === true) {
                        return {feimOpEvent: OperatorEvent.PAUSE_AUTOPLAY};
                    }

                    return {feimOpEvent: OperatorEvent._PAUSE_WHEN_IDLE, payload};
                }

                return {feimOpEvent: OperatorEvent._PAUSE_WHEN_IDLE};
            case "oprg_GameResume": {
                const payload = rgData.payload as IPausePayload;

                return {feimOpEvent: OperatorEvent._RESUME, payload};
            }
            case "oprg_NavigateGame": {
                const payload = rgData.payload as {url: string};

                return {feimOpEvent: OperatorEvent._NAVIGATE_GAME, payload};
            }
            case "oprg_DebugSettings":
                post("gprg_DebugSettings", { id: `Using FEIM v${version}` });
                break;
            case "oprg_DebugErrors":
                post("gprg_DebugErrors", getErrors());
                break;
            case "oprg_Ping": {
                const payload = rgData.payload as {id?: string};

                if (payload?.id !== undefined)
                {
                    post("gprg_Pong", { id: payload.id });
                }
                else
                {
                    post("gprg_Pong");
                }
                break;
            }
            case "oprg_UpdateBalance":
                return {feimOpEvent: OperatorEvent.REFRESH_BALANCE};
            case "oprg_SetSounds": {
                const payload = rgData.payload as {enableSounds: boolean};
                return {feimOpEvent: OperatorEvent.UPDATE_SETTINGS, payload: {sounds: payload.enableSounds}};
            }
            case "oprg_GetSoundState":
                if (cachedSoundSetting >= 0) {
                    return {feimOpEvent: OperatorEvent.UPDATE_SETTINGS, payload: {sounds: cachedSoundSetting > 0}};
                }
                break;
            default:
                warn(`Unhandled rgMessage ${rgData.rgMessage}`);
        }

        return undefined;
    }

    /**
     * Converts game events to Rg Post Message API
     * Returns undefined if the message has no equivalent in Rg API and should not be sent.
     * Some unsent messages can still have side effects, as some data from them can be cached.
     * @param event
     * @param eventPayload
     */
    public processOutbound (event: GameEvent, eventPayload: unknown): { [index: string]: unknown } | undefined
    {
        // Some FEIM events have no equivalents in Rg API.
        // We have no default handler, this makes sure all new events are explicitly handled here - even if they do nothing.

        switch (event) {
            // Internal events
            case GameEvent.LISTENING: return; // Rg API handshake is done when it's first enabled
            case GameEvent.HELLO: return;
            case GameEvent.CONFIGURE: return;
            case GameEvent.REALITY_CHECK_RESOLVED: return;
            case GameEvent.PAUSED: {
                let payload = eventPayload as IPausePayload | undefined;

                if (payload?.realityCheck) {
                    showExternalRealityCheck();
                }

                return {rgMessage: "gprg_GamePaused", payload};
            }
            case GameEvent.RESUMING_AFTER_PAUSE: {
                let payload = eventPayload as IPausePayload | undefined;

                if (payload?.realityCheck) {
                    closeExternalRealityCheck();
                }

                return {rgMessage: "gprg_GameResumed", payload};
            }
            // Events from the game
            case GameEvent.GAME_LOAD_STARTED: return;
            case GameEvent.GAME_LOAD_PROGRESS: return;
            case GameEvent.GAME_LOAD_COMPLETED: return {rgMessage: "gprg_GameReady"};
            case GameEvent.ROUND_STARTED: {
                // Round start payloads are identical, but optional in FEIM
                let payload = eventPayload as IRoundStartedData | undefined;
                if (payload === undefined) {
                    payload = {balance: cachedBalance};
                }

                return {rgMessage: "gprg_GameRoundStart", payload};
            }
            case GameEvent.ROUND_FINISHED: {
                // Round end payloads are identical, but optional in FEIM
                let payload = eventPayload as IRoundFinishedData | undefined;
                if (payload === undefined || payload.balance === undefined) {
                    payload = {balance: cachedBalance};
                }

                return {rgMessage: "gprg_GameRoundEnd", payload};
            }
            case GameEvent.FEATURE_STARTED: return;
            case GameEvent.FEATURE_FINISHED: return;
            case GameEvent.AUTOPLAY_STARTED: return;
            case GameEvent.AUTOPLAY_FINISHED: return;
            case GameEvent.REQUEST_VIEWPORT_CONFIG: return;
            case GameEvent.BALANCE_UPDATE:
                cachedBalance = eventPayload as number;

                return;
            case GameEvent.BET_UPDATE: return;
            case GameEvent.WIN_UPDATE: return;
            case GameEvent.UPDATE_SETTINGS: {
                const payload = {enableSounds: (eventPayload as IGameSettings).sounds};

                return {rgMessage: "gprg_Settings", payload};
            }
            case GameEvent.ERROR_MESSAGE: return;
            case GameEvent.EXIT_GAME: return;
            case GameEvent.GOTO_GAME: return;
            case GameEvent.OPEN_QUICK_DEPOSIT: return {rgMessage: "gprg_OpenQuickDeposit"};
            case GameEvent.REPORT_SESSION_DETAILS: return {rgMessage: "gprg_RealityCheckData", payload: eventPayload};
            case GameEvent.USER_ACTION: return {rgMessage: "gprg_UserAction", payload: eventPayload};
            case GameEvent.REPORT_UI_STATE: return;
        }
    }
}